import React, { ForwardedRef, LegacyRef, forwardRef } from 'react'
import { Link } from 'gatsby'

import { ReactComponent as NewSVG } from './NewSVG.svg'
import classNames from 'classnames'
import styles from './HeaderMenu.module.scss'
import { isLocalLink } from '../../../helpers/url'

export interface HeaderMenuProps {
  leftColumns?: ColumnProps[]
  rightColumn?: ColumnProps
  asidePoster?: React.ReactNode
  rightColumnWidth?: number
  leftColumnWidth?: number
  darkMode?: boolean
  bottomLeftComponent?: React.ReactNode
  bottomRightComponent?: React.ReactNode
  isOpen?: boolean
}

function HeaderMenu(
  {
    leftColumns,
    rightColumn,
    asidePoster,
    darkMode,
    isOpen,
    bottomLeftComponent,
    bottomRightComponent,
    rightColumnWidth,
    leftColumnWidth,
  }: HeaderMenuProps,
  ref: ForwardedRef<HTMLElement>
) {
  const onlyLeftColumn = leftColumns && !rightColumn

  return (
    <div
      ref={ref as LegacyRef<HTMLDivElement>}
      className={classNames(styles.outContainer, { [styles.isOpen]: isOpen })}
    >
      <div className={classNames(styles.container, { [styles.darkHeaderMenu]: darkMode })}>
        <section className={classNames(styles.dropdown)}>
          {leftColumns && (
            <div
              className={classNames(styles.columns, {
                [styles.darkColumns]: darkMode,
                [styles.onlyLeft]: onlyLeftColumn,
                [styles.oneLeftColumn]: leftColumns.length === 1,
              })}
            >
              <div className={styles.columnsContainer}>
                {leftColumns.map(({ title, list, centerAligned }, index) => (
                  <Column
                    key={title ?? index}
                    title={title}
                    list={list}
                    rightColumnWidth={rightColumnWidth}
                    leftColumnWidth={leftColumnWidth}
                    darkMode={darkMode}
                    centerAligned={centerAligned}
                  />
                ))}
              </div>
              {bottomLeftComponent && <div className={styles.bottomRow}>{bottomLeftComponent}</div>}
            </div>
          )}

          {rightColumn && (
            <aside className={classNames(styles.rightSection, { [styles.darkRightSection]: darkMode })}>
              {rightColumn.title && <p className={styles.rightSectionLabel}>{rightColumn.title}</p>}
              <div className={styles.rightColumns} style={{ width: rightColumnWidth }}>
                {rightColumn.list.map(({ title, url, description, icon: Icon }, index) =>
                  isLocalLink(url) ? (
                    <Link key={`${index}-${url}`} to={url} className={styles.link}>
                      <li className={styles.rightSectionRow}>
                        {Icon && (
                          <div className={styles.icon}>
                            <Icon />
                          </div>
                        )}
                        <h3 className={styles.linkTitle}>{title}</h3>
                        {description && <p className={styles.linkDescription}>{description}</p>}
                      </li>
                    </Link>
                  ) : (
                    <a key={`${index}-${url}`} href={url} className={styles.link} rel='noreferrer'>
                      <li className={styles.rightSectionRow}>
                        {Icon && (
                          <div className={styles.icon}>
                            <Icon />
                          </div>
                        )}
                        <h3 className={styles.linkTitle}>{title}</h3>
                        {description && <p className={styles.linkDescription}>{description}</p>}
                      </li>
                    </a>
                  )
                )}
                {bottomRightComponent && <div className={styles.bottomRow}>{bottomRightComponent}</div>}
              </div>
            </aside>
          )}

          {asidePoster && (
            <aside
              className={classNames(styles.rightSection, styles.asidePoster, {
                [styles.darkAsidePoster]: darkMode,
              })}
            >
              {asidePoster}
            </aside>
          )}
        </section>
      </div>
    </div>
  )
}

interface ColumnProps {
  title?: string
  rightColumnWidth?: number
  leftColumnWidth?: number
  darkMode?: boolean
  centerAligned?: boolean
  list: Array<{
    title: string | React.ReactNode
    url: string
    description?: string
    useCasesLink?: string
    icon?: React.FC<React.SVGProps<SVGSVGElement>>
    isNew?: boolean
  }>
}

function Column({ title, list, darkMode, centerAligned, leftColumnWidth }: ColumnProps) {
  return (
    <>
      <div className={styles.column} style={{ width: leftColumnWidth }}>
        {title && <p className={styles.label}>{title}</p>}
        <div className={styles.rows}>
          {list.map(({ title, url, description, icon: Icon, isNew }, index) => (
            <div key={`${url}-${index}`} className={classNames({ [styles.rowContainer]: isNew })}>
              {isLocalLink(url) ? (
                !isNew ? (
                  <Link to={url}>
                    <div
                      className={classNames(styles.columnLink, {
                        [styles.darkColumnLink]: darkMode,
                        [styles.centerAligned]: centerAligned,
                      })}
                    >
                      {Icon && (
                        <div className={styles.icon}>
                          <Icon />
                        </div>
                      )}

                      <li className={classNames(styles.row)}>
                        <h3 className={styles.linkTitle}>{title}</h3>
                        {description && <p className={styles.linkDescription}>{description}</p>}
                      </li>
                    </div>
                  </Link>
                ) : (
                  <Link to={url}>
                    <div
                      className={classNames(styles.columnLink, styles.columnLinkNew, {
                        [styles.darkColumnLinkNew]: darkMode,
                        [styles.centerAligned]: centerAligned,
                      })}
                    >
                      <h5 className={styles.newLabel}>What&apos;s new</h5>

                      <li className={classNames(styles.row)}>
                        <h3 className={styles.linkTitle}>{title}</h3>
                        {description && <p className={styles.linkDescription}>{description}</p>}
                      </li>

                      <NewSVG className={styles.newIcon} />
                    </div>
                  </Link>
                )
              ) : !isNew ? (
                <a href={url} rel='noreferrer'>
                  <div
                    className={classNames(styles.columnLink, {
                      [styles.darkColumnLink]: darkMode,
                      [styles.centerAligned]: centerAligned,
                    })}
                  >
                    {Icon && (
                      <div className={styles.icon}>
                        <Icon />
                      </div>
                    )}

                    <li className={classNames(styles.row)}>
                      <h3 className={styles.linkTitle}>{title}</h3>
                      {description && <p className={styles.linkDescription}>{description}</p>}
                    </li>
                  </div>
                </a>
              ) : (
                <a href={url} rel='noreferrer'>
                  <div
                    className={classNames(styles.columnLink, styles.columnLinkNew, {
                      [styles.darkColumnLinkNew]: darkMode,
                      [styles.centerAligned]: centerAligned,
                    })}
                  >
                    <h5 className={styles.newLabel}>What&apos;s new</h5>
                    <li className={classNames(styles.row)}>
                      <h3 className={styles.linkTitle}>{title}</h3>
                      {description && <p className={styles.linkDescription}>{description}</p>}
                    </li>

                    <NewSVG className={styles.newIcon} />
                  </div>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default forwardRef(HeaderMenu)
